import React, { useState, useEffect, useLayoutEffect, useCallback, useContext, useRef } from "react";
import withWidth, { isWidthUp } from "@material-ui/core/withWidth";
import "emoji-mart/css/emoji-mart.css";
import MicRecorder from "mic-recorder-to-mp3";
import clsx from "clsx";
import { useParams } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import InputBase from "@material-ui/core/InputBase";
import CircularProgress from "@material-ui/core/CircularProgress";
import { green } from "@material-ui/core/colors";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import IconButton from "@material-ui/core/IconButton";
import MoodIcon from "@material-ui/icons/Mood";
import SendIcon from "@material-ui/icons/Send";
import ScheduleIcon from "@material-ui/icons/Schedule";
import CancelIcon from "@material-ui/icons/Cancel";
import ClearIcon from "@material-ui/icons/Clear";
import MicIcon from "@material-ui/icons/Mic";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { FormControlLabel, Switch, Typography, List, ListItem, ListItemText } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { isString, isEmpty, isObject, has } from "lodash";
import CollectionsBookmarkIcon from '@material-ui/icons/CollectionsBookmark';
import DescriptionIcon from '@material-ui/icons/Description';
import AutoAwesomeIcon from '@material-ui/icons/EmojiObjects'; // AI icon (lightbulb)

import { i18n } from "../../translate/i18n";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { ListItemIcon, Divider } from "@material-ui/core";
import GPTSuggestionsModal from "../GPTSuggestionsModal";
import ConversationSummaryModal from "../ConversationSummaryModal";
import SalesOpportunitiesModal from "../SalesOpportunitiesModal";
import SentimentTrendsModal from "../SentimentTrendsModal";
import { getGPTSuggestions } from "../../services/apiGpt";
import api from "../../services/api";
import { hapticTap } from "../../utils/haptics";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { EditMessageContext } from "../../context/EditingMessage/EditingMessageContext";

import useQuickMessages from "../../hooks/useQuickMessages";
import { getQuickMenuTemplates } from "../../services/apiTemplates";
import SendTemplateModal from "../SendTemplateModal";

import Compressor from 'compressorjs';
import LinearWithValueLabel from "./ProgressBarCustom";
import MarkdownWrapper from "../MarkdownWrapper";
import ScheduleMessageModal from "../ScheduleMessageModal";
import MediaPreviewModal from "../MediaPreviewModal";
import ChannelSwitcherDropdown from "../ChannelSwitcherDropdown";
import VoiceCallButton from "../VoiceCallButton";
import SoftphoneDialer from "../SoftphoneDialer";
import ErrorBoundary from "../ErrorBoundary";

const Mp3Recorder = new MicRecorder({ bitRate: 128 });

// Lazy — emoji-mart (Picker + emoji data, heavy) loads only when the
// picker first opens, not in the entry bundle.
const Picker = React.lazy(() =>
  import("emoji-mart").then((m) => ({ default: m.Picker }))
);

const useStyles = makeStyles((theme) => ({
  buttonsRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4,
    [theme.breakpoints.down("xs")]: {
      overflowX: "auto",
      flexWrap: "nowrap",
      WebkitOverflowScrolling: "touch",
      "& > *": { flexShrink: 0 },
    },
  },
  mainWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
    backgroundColor: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    // The composer is a normal bottom flex item; it lands above the keyboard
    // because initKeyboardViewport shrinks the app shell (--app-vh) to the
    // visible height. (Earlier marginBottom:var(--kb-inset) hack removed — it
    // fought WKWebView's auto-scroll and never lifted reliably.)
    // Desktop: pure safe-area (0px on devices without insets), so no
    // visual regression on browser viewports. Mobile (sm-down): floor
    // the inset at 8px so Samsung/Android gesture bars don't collapse
    // the input against the bottom edge. Single source of truth for the
    // bottom safe-area inset; ancestor layouts must NOT also pad-bottom
    // with env(safe-area-inset-bottom) or the insets stack.
    paddingBottom: "env(safe-area-inset-bottom, 0px)",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "max(env(safe-area-inset-bottom, 0px), 8px)",
    },
  },

  dropOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1500,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      theme.palette.type === "dark"
        ? "rgba(15, 23, 42, 0.85)"
        : "rgba(15, 23, 42, 0.78)",
    WebkitBackdropFilter: "blur(4px)", // older Android System WebView needs the prefix
    backdropFilter: "blur(4px)",
    pointerEvents: "none", // let drop event reach window listener
  },
  dropOverlayInner: {
    border: "3px dashed #ffffff",
    borderRadius: 18,
    padding: "48px 64px",
    textAlign: "center",
    color: "#ffffff",
    maxWidth: 520,
  },
  dropOverlayIcon: {
    fontSize: 72,
    marginBottom: 12,
    opacity: 0.95,
  },
  dropOverlayTitle: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 8,
  },
  dropOverlaySub: {
    fontSize: 15,
    opacity: 0.85,
  },

  newMessageBox: {
    width: "100%",
    padding: "7px",
    alignItems: "center",
    position: "relative", // Required for absolute positioned dropdowns
    [theme.breakpoints.down("xs")]: {
      padding: "2px 6px",
    },
  },

  messageInputWrapper: {
    padding: 6,
    marginRight: 7,
    //background: "#fff",
    border: "1px solid #ccc",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    [theme.breakpoints.down("xs")]: {
      padding: 4,
      marginRight: 4,
    },
  },

  messageInput: {
    paddingLeft: 10,
    flex: 1,
    border: "none",
  },

  // Autocomplete dropdown RTL styles
  autocompleteListbox: {
    direction: theme.direction === "rtl" ? "rtl" : "ltr",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    maxHeight: "300px !important",
    "& .MuiAutocomplete-option": {
      direction: theme.direction === "rtl" ? "rtl" : "ltr",
      textAlign: theme.direction === "rtl" ? "right" : "left",
      display: "block",
      width: "100%",
      whiteSpace: "normal",
      wordWrap: "break-word",
      overflow: "visible",
      maxHeight: "none",
      paddingRight: theme.direction === "rtl" ? "16px !important" : "6px",
      paddingLeft: theme.direction === "rtl" ? "6px" : "16px !important",
    },
  },
  
  autocompletePaper: {
    direction: theme.direction === "rtl" ? "rtl" : "ltr",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    maxWidth: "100%",
    width: "100%",
  },

  inputMessageWrapper: {
    display: 'flex',
    position: 'relative', // Required for absolute positioned quick answers dropdown
    flex: 1,
  },

  sendMessageIcons: {
    color: "grey",
    transition: "color 0.18s ease",
  },
  // The primary "send" icon should look like the main action. Tinted
  // brand-purple, gentle scale-down on click for tactile feedback.
  sendPrimaryButton: {
    transition: "transform 0.12s ease, background-color 0.18s ease",
    "& svg": {
      color: "#25d366",
      transition: "color 0.18s ease",
    },
    "&:hover svg": {
      color: "#1ebe5d",
    },
    "&:active": {
      transform: "scale(0.92)",
    },
    "&.Mui-disabled svg": {
      color: theme.palette.action.disabled,
    },
  },

  uploadInput: {
    display: "none",
  },

  viewMediaInputWrapper: {
    display: "flex",
    padding: "10px 13px",
    position: "relative",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#eee",
    borderTop: "1px solid rgba(0, 0, 0, 0.12)",
  },

  emojiBox: {
    position: "absolute",
    bottom: 120,
    zIndex: 99,
    // RTL: align emoji picker to right
    right: theme.direction === "rtl" ? 0 : "auto",
    left: theme.direction === "rtl" ? "auto" : 0,
    width: "100%",
    maxWidth: 420,
    borderTop: "1px solid #e8e8e8",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
    // Enter/exit is now owned by the <Grow> wrapper around this element
    // (see EmojiOptions) so the picker animates out instead of hard-
    // unmounting; the old fadeInUp keyframe only covered the entrance.
    "& section": {
      width: "100%"
    },
    "& .emoji-mart": {
      border: "none",
      borderRadius: 12,
      width: "100% !important",
    },
    "& .emoji-mart-scroll": {
      height: 280,
    },
    "& .emoji-mart-bar": {
      borderColor: theme.palette.divider,
    },
    "& .emoji-mart-search input": {
      borderColor: theme.palette.divider,
      borderRadius: 8,
    },
    // Mobile: smaller emojis
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      bottom: 100,
      borderRadius: '12px 12px 0 0',
      "& .emoji-mart-scroll": {
        height: 220,
      },
      "& .emoji-mart-emoji span": {
        width: '22px !important',
        height: '22px !important',
      },
      "& .emoji-mart-category-label span": {
        fontSize: 12,
      },
    },
  },
  
  emojiButton: {
    transition: "all 0.2s ease",
    borderRadius: 8,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  
  emojiButtonActive: {
    backgroundColor: theme.palette.primary.main + "20",
    color: theme.palette.primary.main,
    "& .MuiSvgIcon-root": {
      color: theme.palette.primary.main,
    },
  },
  
  "@keyframes emojiPulse": {
    "0%": {
      transform: "scale(1)",
    },
    "50%": {
      transform: "scale(1.2)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
  
  emojiAdded: {
    animation: "$emojiPulse 0.3s ease",
  },

  circleLoading: {
    color: green[500],
    opacity: "70%",
    position: "absolute",
    top: "20%",
    left: "50%",
    marginLeft: -12,
  },

  audioLoading: {
    color: green[500],
    opacity: "70%",
  },

  recorderWrapper: {
    display: "flex",
    alignItems: "center",
    alignContent: "middle",
  },

  cancelAudioIcon: {
    color: "red",
  },

  sendAudioIcon: {
    color: "green",
  },

  replyginMsgWrapper: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 8,
    paddingLeft: 73,
    paddingRight: 7,
  },

  replyginMsgContainer: {
    flex: 1,
    marginRight: 5,
    overflowY: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  replyginMsgBody: {
    padding: 10,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  replyginContactMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  replyginSelfMsgSideColor: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  messageQuickAnswersWrapper: {
    margin: "10px 0px",
    position: "absolute",
    bottom: "100%",
    background: theme.palette.type === "dark" ? "#131614" : "#ffffff",
    padding: "10px",
    border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid #CCC",
    borderRadius: "12px",
    // RTL: align to right side
    right: theme.direction === "rtl" ? 0 : "auto",
    left: theme.direction === "rtl" ? "auto" : 0,
    width: "100%",
    maxWidth: "400px",
    maxHeight: 300,
    overflowY: "auto",
    zIndex: 1000,
    boxShadow: theme.palette.type === "dark" 
      ? "0 8px 32px rgba(0,0,0,0.4)" 
      : "0 8px 32px rgba(0,0,0,0.15)",
    textAlign: theme.direction === "rtl" ? "right" : "left",
    direction: theme.direction === "rtl" ? "rtl" : "ltr",
    "& li, & .MuiListItem-root": {
      listStyle: "none",
      direction: theme.direction === "rtl" ? "rtl" : "ltr",
      textAlign: theme.direction === "rtl" ? "right" : "left",
      "& a, & .MuiListItemText-root": {
        display: "block",
        padding: "10px 12px",
        borderRadius: "8px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: theme.palette.type === "dark" ? "#fff" : "#131614",
        direction: "inherit",
        textAlign: "inherit",
        unicodeBidi: "plaintext",
        "&:hover": {
          background: theme.palette.type === "dark" ? "rgba(99, 102, 241, 0.15)" : "#f1f5f9",
          cursor: "pointer",
        },
      },
    },
  },
  
  listItem: {
    direction: theme.direction === "rtl" ? "rtl" : "ltr",
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
}));

// Skin tone modifiers
const SKIN_TONES = [
  { id: 0, emoji: '👍', label: 'Default', modifier: '' },
  { id: 1, emoji: '👍🏻', label: 'Light', modifier: '\u{1F3FB}' },
  { id: 2, emoji: '👍🏼', label: 'Medium-Light', modifier: '\u{1F3FC}' },
  { id: 3, emoji: '👍🏽', label: 'Medium', modifier: '\u{1F3FD}' },
  { id: 4, emoji: '👍🏾', label: 'Medium-Dark', modifier: '\u{1F3FE}' },
  { id: 5, emoji: '👍🏿', label: 'Dark', modifier: '\u{1F3FF}' },
];

const EmojiOptions = (props) => {
  const { disabled, showEmoji, setShowEmoji, handleAddEmoji, onBeforeOpen } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const [justAdded, setJustAdded] = useState(false);
  const [selectedSkinTone, setSelectedSkinTone] = useState(3); // Default to medium (🏽)
  
  // Check if mobile for smaller emoji size
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  
  const handleToggleEmoji = (e) => {
    // Save cursor position BEFORE opening emoji picker
    if (!showEmoji && onBeforeOpen) {
      onBeforeOpen();
    }
    setShowEmoji((prevState) => !prevState);
  };
  
  const handleEmojiSelect = (emoji) => {
    // Add selected skin tone to hand emojis
    const handEmojis = ['👍', '👎', '👋', '🙏', '👏', '🙌', '🤝', '✋', '🖐', '👌', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝', '✌', '🖕', '💪', '🦶', '🦵', '👂', '🦻', '👃', '👶', '🧒', '👦', '👧', '🧑', '👱', '👨', '👩', '🧔', '👴', '👵', '🙍', '🙎', '🙅', '🙆', '💁', '🙋', '🧏', '🙇', '🤦', '🤷', '👮', '🕵', '💂', '👷', '🤴', '👸', '👳', '👲', '🧕', '🤵', '👰', '🤰', '🤱', '👼', '🎅', '🤶', '🦸', '🦹', '🧙', '🧚', '🧛', '🧜', '🧝', '🧞', '🧟', '💆', '💇', '🚶', '🧍', '🧎', '🏃', '💃', '🕺', '🕴', '🧖', '🧗', '🤸', '🏌', '🏇', '⛷', '🏂', '🏋', '🤼', '🤽', '🤾', '🤺', '⛹', '🏊', '🚣', '🧘', '🛀', '🛌'];
    
    let emojiNative = emoji.native;
    
    // Check if this emoji supports skin tones
    const baseEmoji = emojiNative ? emojiNative.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '') : '';
    if (handEmojis.includes(baseEmoji) && selectedSkinTone > 0) {
      // Add selected skin tone modifier
      emojiNative = baseEmoji + SKIN_TONES[selectedSkinTone].modifier;
      emoji = { ...emoji, native: emojiNative };
    }
    
    handleAddEmoji(emoji);
    // Trigger pulse animation
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 300);
  };
  
  return (
    <>
      <IconButton
        aria-label="emojiPicker"
        component="span"
        disabled={disabled}
        onClick={handleToggleEmoji}
        className={clsx(classes.emojiButton, {
          [classes.emojiButtonActive]: showEmoji,
          [classes.emojiAdded]: justAdded,
        })}
      >
        <MoodIcon className={clsx(classes.sendMessageIcons, {
          [classes.emojiButtonActive]: showEmoji,
        })} />
      </IconButton>
      {/* Grow gives the picker a real exit transition instead of the old
          hard-unmount ({showEmoji ? <div/> : null}); unmountOnExit keeps
          the (heavy) Picker component out of the DOM while closed, same
          as before, just after the animation finishes instead of instantly. */}
      <Grow in={showEmoji} timeout={200} unmountOnExit>
        <div className={classes.emojiBox}>
          {/* Skin tone selector */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px',
            background: isDark ? '#2a2f32' : '#f0f0f0',
            borderBottom: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #ddd',
            gap: '8px',
          }}>
            <span style={{ fontSize: '12px', marginRight: '8px', color: isDark ? 'rgba(255,255,255,0.7)' : '#666' }}>צבע עור:</span>
            {SKIN_TONES.map((tone) => (
              <button
                key={tone.id}
                onClick={() => setSelectedSkinTone(tone.id)}
                style={{
                  fontSize: '20px',
                  minWidth: 44,
                  minHeight: 44,
                  padding: '10px 12px',
                  border: selectedSkinTone === tone.id ? '2px solid #075e54' : '2px solid transparent',
                  borderRadius: '8px',
                  background: selectedSkinTone === tone.id ? (isDark ? 'rgba(37,211,102,0.18)' : '#dcfce7') : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                title={tone.label}
              >
                {tone.emoji}
              </button>
            ))}
          </div>
          <React.Suspense fallback={null}>
          <Picker
            theme={theme.palette.type}
            perLine={isMobile ? 9 : 12}
            showPreview={!isMobile}
            showSkinTones={false}
            skin={3}
            emojiSize={isMobile ? 22 : 28}
            native={true}
            onSelect={handleEmojiSelect}
            title={i18n.t("messageInput.chooseEmoji")}
            emoji="raised_hands"
            skinEmoji="raised_hands"
            recent={["+1", "pray", "raised_hands", "wave", "ok_hand", "clap", "heart", "fire", "star", "rocket"]}
            i18n={{
              search: i18n.t("messageInput.emojiPicker.search"),
              clear: i18n.t("messageInput.emojiPicker.clear"),
              notfound: i18n.t("messageInput.emojiPicker.notfound"),
              categories: {
                search: i18n.t("messageInput.emojiPicker.categories.search"),
                recent: i18n.t("messageInput.emojiPicker.categories.recent"),
                smileys: i18n.t("messageInput.emojiPicker.categories.smileys"),
                people: i18n.t("messageInput.emojiPicker.categories.people"),
                nature: i18n.t("messageInput.emojiPicker.categories.nature"),
                foods: i18n.t("messageInput.emojiPicker.categories.foods"),
                activity: i18n.t("messageInput.emojiPicker.categories.activity"),
                places: i18n.t("messageInput.emojiPicker.categories.places"),
                objects: i18n.t("messageInput.emojiPicker.categories.objects"),
                symbols: i18n.t("messageInput.emojiPicker.categories.symbols"),
                flags: i18n.t("messageInput.emojiPicker.categories.flags"),
              },
            }}
          />
</React.Suspense>
        </div>
      </Grow>
    </>
  );
};

const SignSwitch = (props) => {
  const { width, setSignMessage, signMessage } = props;
  if (isWidthUp("md", width)) {
    return (
      <FormControlLabel
        style={{ marginRight: 7, color: "gray" }}
        label={i18n.t("messagesInput.signMessage")}
        labelPlacement="start"
        control={
          <Switch
            size="small"
            checked={signMessage}
            onChange={(e) => {
              setSignMessage(e.target.checked);
            }}
            name="signMessage"
            color="primary"
          />
        }
      />
    );
  }
  return null;
};

const FileInput = (props) => {
  const { handleChangeMedias, disableOption } = props;
  const classes = useStyles();
  return (
    <>
      <input
        multiple
        type="file"
        id="upload-button"
        disabled={disableOption}
        className={classes.uploadInput}
        onChange={handleChangeMedias}
      />
      <label htmlFor="upload-button">
        <IconButton
          aria-label="upload"
          component="span"
          disabled={disableOption}
        >
          <AttachFileIcon className={classes.sendMessageIcons} />
        </IconButton>
      </label>
    </>
  );
};

const ActionButtons = (props) => {
  const {
    inputMessage,
    loading,
    recording,
    ticketStatus,
    handleSendMessage,
    handleCancelAudio,
    handleUploadAudio,
    handleStartRecording,
    handleScheduleClick,
    disableOption,
  } = props;
  const classes = useStyles();
  if (inputMessage) {
    return (
      <>
        <IconButton
          aria-label="scheduleMessage"
          component="span"
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleScheduleClick}
          disabled={disableOption}
          title="Schedule message"
        >
          <ScheduleIcon className={classes.sendMessageIcons} />
        </IconButton>
        <IconButton
          aria-label="sendMessage"
          component="span"
          onMouseDown={(e) => e.preventDefault()}
          className={classes.sendPrimaryButton}
          onClick={handleSendMessage}
          disabled={disableOption}
        >
          <SendIcon />
        </IconButton>
      </>
    );
  } else if (recording) {
    return (
      <div className={classes.recorderWrapper}>
        <IconButton
          aria-label="cancelRecording"
          component="span"
          fontSize="large"
          disabled={disableOption}
          onClick={handleCancelAudio}
        >
          <HighlightOffIcon className={classes.cancelAudioIcon} />
        </IconButton>
        {loading ? (
          <div>
            <CircularProgress className={classes.audioLoading} />
          </div>
        ) : (
          <RecordingTimer />
        )}

        <IconButton
          aria-label="sendRecordedAudio"
          component="span"
          onClick={handleUploadAudio}
          disabled={disableOption}
        >
          <CheckCircleOutlineIcon className={classes.sendAudioIcon} />
        </IconButton>
      </div>
    );
  } else {
    return (
      <IconButton
        aria-label="showRecorder"
        component="span"
        onMouseDown={(e) => e.preventDefault()}
        disabled={disableOption}
        onClick={handleStartRecording}
      >
        <MicIcon className={classes.sendMessageIcons} />
      </IconButton>
    );
  }
};

const CustomInput = (props) => {
  const {
    loading,
    inputRef,
    cursorPositionRef,
    ticketStatus,
    inputMessage,
    setInputMessage,
    handleSendMessage,
    handleInputPaste,
    disableOption,
    draftKey,
    setFileBar
  } = props;
  const classes = useStyles();
  const [quickMessages, setQuickMessages] = useState([]);
  const [options, setOptions] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);

  const { user } = useContext(AuthContext);

  const { list: listQuickMessages } = useQuickMessages();

  useEffect(() => {
    async function fetchData() {
      const messages = await listQuickMessages();
      const options = messages.map((m) => {
        let truncatedMessage = m.message;
        if (isString(truncatedMessage) && truncatedMessage.length > 35) {
          truncatedMessage = m.message.substring(0, 35) + "...";
        }
        return {
          value: m.message,
          label: `/${m.shortcode} - ${truncatedMessage}`,
        };
      });
      setQuickMessages(options);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFileBar(false)
    // Trailing debounce — a synchronous localStorage write per keystroke
    // is main-thread disk I/O and reads as typing latency on phones.
    clearTimeout(window.__draftTimer);
    window.__draftTimer = setTimeout(
      () => localStorage.setItem(draftKey, inputMessage),
      250
    );
    if (
      isString(inputMessage) &&
      !isEmpty(inputMessage) &&
      inputMessage.length === 1
    ) {
      const firstWord = inputMessage.charAt(0);
      setPopupOpen(firstWord.indexOf("/") > -1);
      setOptions(quickMessages);
    } else if(
      isString(inputMessage) &&
    !isEmpty(inputMessage) &&
    inputMessage.length > 1) {
      const firstWord = inputMessage.charAt(0);
      setPopupOpen(firstWord.indexOf("/") > -1);
      const filteredOptions = quickMessages.filter(
        (m) => m.label.indexOf(inputMessage) > -1
      );
      setOptions(filteredOptions);
    } else {
      setPopupOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMessage]);

  const onPaste = (e) => {
    if (ticketStatus === "open") {
      handleInputPaste(e);
    }
  };

  const renderPlaceholder = () => {
    if (ticketStatus === "open") {
      return i18n.t("messagesInput.placeholderOpen");
    }
    return i18n.t("messagesInput.placeholderClosed");
  };

  const setInputRef = useCallback((input) => {
    if (input) {
      inputRef.current = input;
    }
  }, [inputRef]);
  const handleKeyDown = (event) => {
    // Enter sends directly; Shift+Enter inserts a newline. Handle Enter at the
    // keydown phase (not keypress) so we can preventDefault the textarea's native
    // newline BEFORE it is inserted — keypress fires too late to retract it and
    // is unreliable in iOS WKWebView. When the "/" quick-answer dropdown is open,
    // let MUI Autocomplete consume Enter to SELECT the highlighted option instead
    // of sending (do not preventDefault in that case).
    if (event.key === "Enter" && !event.shiftKey) {
      // IME guard: while composing (Hebrew niqqud pickers, CJK, emoji search
      // keyboards) Enter commits the composition — never send mid-composition.
      if (event.nativeEvent && event.nativeEvent.isComposing) return;
      // Bail (let Autocomplete select) only when the popup actually has
      // options — an empty "/"-popup (no quick-answer matches, e.g. a message
      // that merely STARTS with "/") must not swallow Enter and leave the
      // message unsendable by keyboard.
      if (popupOpen && options.length > 0) return;
      event.preventDefault();
      if (loading) return;
      handleSendMessage();
      return;
    }
    // Check if the dropdown is open and the key is an arrow key
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.stopPropagation(); // Prevent Autocomplete from handling arrow keys
    }
  };
  // Disable the text field ONLY for a closed ticket — NOT during the in-flight
  // send (loading). MUI v4 putting `disabled` on the focused <textarea>
  // force-blurs it in iOS WKWebView, which dropped the keyboard on every send
  // and reopened it after the POST (the "keyboard refresh" on send). The
  // Send/mic/file action buttons below keep the loading gate via disableOption,
  // and double-send stays guarded (empty-trim return, keydown-Enter loading guard,
  // Send button still disabled while loading). ticketStatus here is already
  // group-coerced, so groups stay enabled exactly as before.
  const inputDisabled = ticketStatus === "closed";
  return (
    <div className={classes.messageInputWrapper}>
      <Autocomplete
        disabled={inputDisabled}
        freeSolo
        open={popupOpen}
        id="grouped-demo"
        inputValue={inputMessage}
        value={null}
        options={options}
        closeIcon={null}
        classes={{
          listbox: classes.autocompleteListbox,
          paper: classes.autocompletePaper,
        }}
        getOptionLabel={(option) => {
          if (isObject(option)) {
            return option.label;
          } else {
            return option;
          }
        }}
        onChange={(event, opt) => {
          if (isObject(opt) && has(opt, "value")) {
            setInputMessage(opt.value);
            setTimeout(() => {
              inputRef.current.scrollTop = inputRef.current.scrollHeight;
            }, 200);
          }
        }}
        onInputChange={(event, opt, reason) => {
          if (reason === "input") {
            setInputMessage(event.target.value);
          }
        }}
        onPaste={onPaste}
        style={{ width: "100%" }}
        renderInput={(params) => {
          const { InputLabelProps, InputProps, ...rest } = params;
          return (
            <InputBase
              {...params.InputProps}
              {...rest}
              disabled={inputDisabled}
              inputRef={setInputRef}
              placeholder={renderPlaceholder()}
              multiline
              className={classes.messageInput}
              maxRows={4}
              onKeyDown={handleKeyDown}
              onSelect={(e) => {
                // Save cursor position on every selection/click for emoji insertion
                if (cursorPositionRef) {
                  cursorPositionRef.current = e.target.selectionStart;
                }
              }}
            />
          );
        }}
      />
    </div>
  );
};

const MessageInputCustom = (props) => {
  const { ticket, showTabGroups } = props;
  const { status: ticketStatus, id: ticketId } = ticket;
  // URL ticket uuid — changes IMMEDIATELY on navigation, whereas ticket.id only
  // updates ~2s later (parent's debounced fetch). Used to key/reset the draft so
  // the previous ticket's text never lingers on screen while the next one loads.
  const { ticketId: routeUuid } = useParams();
  const classes = useStyles();

  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  // Mirror `recording` in a ref so the unmount cleanup can read the latest value.
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  // Release the mic if a voice note is still recording when the composer
  // unmounts (agent switches tickets mid-record) — Mp3Recorder otherwise holds
  // the microphone open for the rest of the session (audit: fe leak).
  useEffect(
    () => () => {
      if (recordingRef.current) {
        try {
          Mp3Recorder.stop().getMp3().catch(() => {});
        } catch (e) {
          /* recorder already stopped */
        }
      }
    },
    []
  );
  const [percentLoading, setPercentLoading] = useState(0);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [quickAnswersPage, setQuickAnswersPage] = useState(1);
  const [hasMoreQuickAnswers, setHasMoreQuickAnswers] = useState(false);
  const [allQuickAnswers, setAllQuickAnswers] = useState([]);
  const QUICK_ANSWERS_PER_PAGE = 8;
  const [typeBar, setTypeBar] = useState(false);
  const [files, setFiles] = useState();
  const [fileBar, setFileBar] = useState(false);
  const [imageURL, setImageURL] = useState(null); // State to store the image URL
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatesBar, setTemplatesBar] = useState(false);
  const [templatesList, setTemplatesList] = useState([]);
  
  // GPT AI Assistant
  const [gptMenuAnchor, setGptMenuAnchor] = useState(null);
  const [gptModalOpen, setGptModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [sentimentModalOpen, setSentimentModalOpen] = useState(false);
  const [gptSuggestions, setGptSuggestions] = useState([]);
  const [gptLoading, setGptLoading] = useState(false);
  const [gptConfidence, setGptConfidence] = useState(0);

  // Phase 3.5 — Channel Switcher: other channels this contact has
  const [channelLinks, setChannelLinks] = useState([]);

  const inputRef = useRef();
  const cursorPositionRef = useRef(null); // Store cursor position for emoji insertion
  // Pending DOM caret index to restore AFTER a *programmatic* insert (emoji /
  // GPT suggestion / quick-answer). Set only by those paths; normal typing
  // never touches it, so the native caret is left alone while the user types.
  const pendingSelectionRef = useRef(null);
  const [currentFile, setCurrentFile] = useState(null);

  const { setReplyingMessage, replyingMessage } =
    useContext(ReplyMessageContext);
  const { setEditingMessage, editingMessage } = useContext(
		EditMessageContext
	);  
  const { user } = useContext(AuthContext);

  const [signMessage, setSignMessage] = useLocalStorage("signOption", false);
  // Key the draft by the URL uuid (immediate) instead of ticket.id (late) so a
  // ticket switch resets/restores the composer at once with no stale-draft flash,
  // and one ticket's text is never written into another's draft slot.
  const draftKey = `draft-${routeUuid}`;
  // True while the visible ticket lags the route during an instant switch:
  // Ticket/index.js keeps the PREVIOUS chat mounted until the new one
  // resolves (perf fix), while the composer already shows the NEW ticket's
  // URL-keyed draft. Sending in that window would post the new draft into
  // the previous customer's ticket — every send path must no-op until the
  // loaded ticket catches up with the route.
  const ticketLagsRoute = () =>
    Boolean(routeUuid && ticket?.uuid && ticket.uuid !== routeUuid);

  // Restore the DOM caret after a *programmatic* insert (emoji / GPT / quick
  // answer). Only fires when pendingSelectionRef was set by one of those paths;
  // plain typing leaves it null so the browser's own caret is never disturbed.
  // rAF lets the controlled inputValue commit + the multiline InputBase reflow
  // before we set the selection range.
  useLayoutEffect(() => {
    if (pendingSelectionRef.current == null) return;
    const pos = pendingSelectionRef.current;
    pendingSelectionRef.current = null;
    const el = inputRef.current;
    if (!el) return;
    const apply = () => {
      try {
        const p = Math.max(0, Math.min(pos, (el.value || "").length));
        el.setSelectionRange(p, p);
      } catch (e) { /* no-op */ }
    };
    requestAnimationFrame(apply);
  }, [inputMessage]);

  // Restore the DOM caret after a *programmatic* insert (emoji / GPT / quick
  // answer). Only fires when pendingSelectionRef was set by one of those paths;
  // plain typing leaves it null so the browser's own caret is never disturbed.
  // rAF lets the controlled inputValue commit + the multiline InputBase reflow
  // before we set the selection range.
  useLayoutEffect(() => {
    if (pendingSelectionRef.current == null) return;
    const pos = pendingSelectionRef.current;
    pendingSelectionRef.current = null;
    const el = inputRef.current;
    if (!el) return;
    const apply = () => {
      try {
        const p = Math.max(0, Math.min(pos, (el.value || "").length));
        el.setSelectionRange(p, p);
      } catch (e) { /* no-op */ }
    };
    requestAnimationFrame(apply);
  }, [inputMessage]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });

    if (editingMessage) {
      // Strip the sign prefix when populating the input for edit so
      // the operator doesn't see their own name in the field. Handles
      // both the legacy `*name:*\n` markdown form (kept for messages
      // signed before 2026-05-19) and the current `name —\n` form.
      const legacyPrefix = `*${user.name}:*\n`;
      const currentPrefix = `${user.name} —\n`;
      if (signMessage && editingMessage.body.startsWith(legacyPrefix)) {
        setInputMessage(editingMessage.body.slice(legacyPrefix.length));
      } else if (signMessage && editingMessage.body.startsWith(currentPrefix)) {
        setInputMessage(editingMessage.body.slice(currentPrefix.length));
      } else {
        setInputMessage(editingMessage.body);
      }
    }

    if (replyingMessage || editingMessage) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [replyingMessage, editingMessage, signMessage, user.name]);
  
  useLayoutEffect(() => {
    // Reset/restore the composer the instant the URL ticket changes — not ~2s
    // later when ticket.id finally loads — so the previous ticket's draft never
    // lingers on screen. Read the draft here (keyed by the immediate URL uuid)
    // to restore this ticket's own text, or clear if it has none.
    // useLayoutEffect (not useEffect) is load-bearing: CustomInput's passive
    // [inputMessage] effect persists "" to the draft key on mount, and React
    // runs child passive effects before parent passive effects — a passive
    // restore here would read storage AFTER that wipe and lose the draft on
    // every composer mount. All layout effects run before ALL passive effects,
    // so this restore always wins the race.
    setInputMessage(localStorage.getItem(`draft-${routeUuid}`) || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeUuid]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
    return () => {
      setShowEmoji(false);
      setFileBar(false);
      setFiles([]);
      setMedias([]);
      setReplyingMessage(null);
      setEditingMessage(null);
      // inputMessage reset is owned by the [routeUuid] effect above (fires
      // immediately on navigation); don't also clear it here on the late
      // ticket.id change or it would wipe the freshly-restored draft.
    };
  }, [ticketId]);

  // Phase 3.5 — Fetch channel links for this contact so we can render ChannelSwitcherDropdown
  useEffect(() => {
    const contactId = ticket?.contact?.id;
    if (!contactId) return;
    api
      .get(`/contacts/${contactId}/channels`)
      .then(({ data }) => setChannelLinks(Array.isArray(data) ? data : []))
      .catch(() => setChannelLinks([]));
  }, [ticket?.contact?.id]);

  // const handleChangeInput = e => {
  // 	if (isObject(e) && has(e, 'value')) {
  // 		setInputMessage(e.value);
  // 	} else {
  // 		setInputMessage(e.target.value)
  // 	}
  // };
  const handleQuickAnswersClick = (item) => {
    if (item.isTemplate) {
      if (ticketLagsRoute()) return; // modal would send into the previous ticket
      // Open template modal
      setSelectedTemplate(item);
      setTemplateModalOpen(true);
      setTypeBar(false);
      setInputMessage("");
    } else {
      // Regular quick message
      setInputMessage(item.message || item);
      setTypeBar(false);
    }
  }; 
  const handleFileClick = async (fileList) => {
    if (ticketLagsRoute()) return; // never post into the previous ticket mid-switch
    setCurrentFile(fileList);
    setFileBar(false);
    // Fetch the image URL from the API
    try {
       await api.post(`/messages/catalog/${ticketId}/${fileList.id}`);
    } catch (error) {
      toastError(error);
      setImageURL(null);
    }
  };
  const handleOpenFileListModal = () => {
    if(!fileBar) {
      fetchFileLists()
    } else {
      setFileBar(false)
    }
  }
  const handleAddEmoji = (e) => {
    const emoji = e.native;
    const cursorPos = cursorPositionRef.current;
    
    setInputMessage((prevState) => {
      // If we have a stored cursor position, insert at that position
      if (cursorPos !== null && cursorPos !== undefined && cursorPos <= prevState.length) {
        const before = prevState.substring(0, cursorPos);
        const after = prevState.substring(cursorPos);
        // Update cursor position for next emoji
        cursorPositionRef.current = cursorPos + emoji.length;
        pendingSelectionRef.current = cursorPos + emoji.length;
        return before + emoji + after;
      }
      // Fallback: append to end
      cursorPositionRef.current = prevState.length + emoji.length;
      pendingSelectionRef.current = prevState.length + emoji.length;
      return prevState + emoji;
    });
  };
  
  // Save cursor position before emoji picker opens (input loses focus)
  const handleSaveCursorPosition = () => {
    if (inputRef.current) {
      cursorPositionRef.current = inputRef.current.selectionStart ?? inputRef.current.value?.length ?? 0;
    }
  };

  const handleChangeMedias = (e) => {
    // 2026-06-12: outbound media is now delivered on FB/IG/Telegram too (backend
    // routes via sendMedia / routeOutboundMedia), so intake is no longer blocked.
    if (!e.target.files) {
      return;
    }

    const selectedMedias = Array.from(e.target.files);
    setMedias((prev) => [...prev, ...selectedMedias]);
  };

  const handleInputPaste = (e) => {
    const pasted = Array.from(e.clipboardData?.files || []);
    if (pasted.length > 0) {
      setMedias((prev) => [...prev, ...pasted]);
    }
  };

  // Visual "Drop files here" overlay state. We use a counter (not a bool)
  // because dragenter/dragleave fire for every nested element the cursor
  // crosses — increment on enter, decrement on leave, show overlay while
  // counter > 0, reset to 0 on drop.
  const dragCounterRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  // Global drag-and-drop + clipboard paste while a ticket is open. Mirrors
  // the UX of WhatsApp Web / Telegram Web: users can drop a file anywhere
  // on the conversation surface (or Cmd+V from the OS clipboard) and the
  // preview modal will open with the file queued for review.
  useEffect(() => {
    if (!ticketId) return undefined;

    const acceptFiles = (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length) return;
      setMedias((prev) => [...prev, ...files]);
    };

    const hasFiles = (e) => {
      const types = e.dataTransfer?.types;
      if (!types) return false;
      // DataTransferItemList vs string array — handle both
      if (typeof types.includes === "function") return types.includes("Files");
      for (let i = 0; i < types.length; i += 1) {
        if (types[i] === "Files") return true;
      }
      return false;
    };

    const onDragEnter = (e) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setIsDragging(true);
    };
    const onDragOver = (e) => {
      // Required: without preventDefault on dragover, the browser will
      // refuse to fire `drop` and instead navigate to the file URL.
      if (hasFiles(e)) {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
      }
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setIsDragging(false);
    };
    const onDrop = (e) => {
      dragCounterRef.current = 0;
      setIsDragging(false);
      if (e.dataTransfer?.files?.length) {
        e.preventDefault();
        acceptFiles(e.dataTransfer.files);
      }
    };
    const onWindowPaste = (e) => {
      // Don't hijack paste when the user is typing into a normal text field
      // and the clipboard only contains text — only react when actual files
      // are present.
      const files = e.clipboardData?.files;
      if (files && files.length) {
        e.preventDefault();
        acceptFiles(files);
      }
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    window.addEventListener("paste", onWindowPaste);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("paste", onWindowPaste);
      dragCounterRef.current = 0;
      setIsDragging(false);
    };
  }, [ticketId]);

  // Wrap CompressorJS (callback-based) into a Promise so we can `await` it
  // inside Promise.all. The previous implementation called Compressor inside
  // a `forEach(async …)` and then a `setTimeout` with no delay — `forEach`
  // does not await async callbacks, so the POST request fired BEFORE any
  // `success(compressed)` callback got the chance to push into FormData.
  // That's why images silently never made it through (videos / PDFs did,
  // because their append was synchronous).
  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      try {
        new Compressor(file, {
          quality: 0.7,
          success: (result) => resolve(result),
          error: (err) => reject(err),
        });
      } catch (err) {
        reject(err);
      }
    });

  const handleUploadMedia = async (eventOrCaption) => {
    if (ticketLagsRoute()) return; // never post into the previous ticket mid-switch
    setLoading(true);
    if (eventOrCaption?.preventDefault) eventOrCaption.preventDefault();
    const captionFromArg =
      typeof eventOrCaption === "string" ? eventOrCaption : "";

    const formData = new FormData();
    formData.append("fromMe", true);

    // 1. Resolve every file (compress images, pass others through) BEFORE
    //    we start building FormData entries — `Promise.all` guarantees we
    //    wait on Compressor callbacks, fixing the race condition above.
    let processed;
    try {
      processed = await Promise.all(
        medias.map(async (media) => {
          if (!media) return null;
          if (media.type && media.type.startsWith("image/")) {
            try {
              return await compressImage(media);
            } catch (err) {
              console.warn("[handleUploadMedia] compression failed, using original", err);
              return media;
            }
          }
          return media;
        })
      );
    } catch (err) {
      console.error(err);
      toastError(err);
      setLoading(false);
      return;
    }

    // 2. Append the (possibly compressed) blobs to FormData. We pass the
    //    third `name` arg explicitly so multer on the backend always sees
    //    a filename, even when Compressor produces a Blob without one.
    processed.forEach((media) => {
      if (!media) return;
      const fileName =
        media.name || `media-${Date.now()}.${(media.type || "bin").split("/")[1] || "bin"}`;
      formData.append("medias", media, fileName);
      // Per-file caption fallback = filename. The user-typed caption (if any)
      // is appended once below as the "ticket-level" body.
      formData.append("body", captionFromArg || fileName);
    });

    if (captionFromArg) {
      // Some legacy backend code checks the LAST `body` value for a global
      // caption; ensure it wins when the user actually typed one.
      formData.set("caption", captionFromArg);
    }

    try {
      await api.post(`/messages/${ticketId}`, formData, {
        onUploadProgress: (event) => {
          if (!event.total) return;
          const progress = Math.round((event.loaded * 100) / event.total);
          setPercentLoading(progress);
        },
      });
      setMedias([]);
    } catch (err) {
      // Previously the catch was nested inside .then().catch() and only
      // logged to console — leaving the UI stuck with loading=true and the
      // stale media preview. Now we always reset state and notify the user.
      console.error("Error uploading media:", err);
      toastError(err);
    } finally {
      setLoading(false);
      setPercentLoading(0);
    }
  }

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    if (ticketLagsRoute()) return; // never post into the previous ticket mid-switch
    //if (disableOption) return
    hapticTap(); // native-only light tap on send; no-op on web
    setLoading(true);

    const message = {
      read: 1,
      fromMe: true,
      mediaUrl: "",
      // Plain agent-name prefix + em-dash separator + newline. The
      // previous `*name:*` markdown wrap rendered bold but inherited
      // the bubble's text-on-tint colour, which on light-green
      // outgoing bubbles in /chats produced a barely-legible label
      // (Raviv flagged on 2026-05-19). Plain text + em-dash uses the
      // bubble's default body colour, so contrast matches the rest
      // of the message and the agent name stays clearly readable
      // regardless of bubble theme.
      body: signMessage
        ? `${user?.name} —\n${inputMessage.trim()}`
        : inputMessage.trim(),
      quotedMsg: replyingMessage,
    };
    try {
		if (editingMessage !== null) {
			await api.post(`/messages/edit/${editingMessage.id}`, message);
		}
		else {
			await api.post(`/messages/${ticketId}`, message);
		}
      // Only clear the composer/draft on a SUCCESSFUL send — a failed send must
      // keep the user typed text + draft so they can retry (was cleared
      // unconditionally after the catch, losing the message on any error).
      localStorage.removeItem(draftKey);
      setInputMessage("");
      setShowEmoji(false);
      setReplyingMessage(null);
      setEditingMessage(null);
      inputRef.current?.focus({ preventScroll: true });
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const searchTerm = inputMessage.substring(1).toLowerCase();
        
        // Fetch quick messages
        const { data } = await api.get("/quick-messages/list/", {
          params: { searchParam: searchTerm },
        });
        
        // Fetch templates
        let templates = [];
        try {
          const templateData = await getQuickMenuTemplates();
          templates = templateData
            .filter(t => !searchTerm || t.name.toLowerCase().includes(searchTerm))
            .map(t => ({
              ...t,
              shortcut: `📋 ${t.name}`,
              message: t.preview || t.bodyText?.substring(0, 50) + "...",
              isTemplate: true,
            }));
        } catch (templateErr) {
          console.log("Could not load templates:", templateErr);
        }
        
        // Combine quick messages and templates
        const combined = [...data.quickAnswers, ...templates];
        
        // Store all items and show first page
        setAllQuickAnswers(combined);
        setQuickAnswersPage(1);
        setHasMoreQuickAnswers(combined.length > QUICK_ANSWERS_PER_PAGE);
        setQuickAnswer(combined.slice(0, QUICK_ANSWERS_PER_PAGE));
        
        if (combined.length > 0) {
          setTypeBar(true);
        } else {
          setTypeBar(false);
        }
      } catch (err) {
        setTypeBar(false);
      }
    } else {
      setTypeBar(false);
      setQuickAnswersPage(1);
      setAllQuickAnswers([]);
    }
  };
  
  const handleLoadMoreQuickAnswers = () => {
    const nextPage = quickAnswersPage + 1;
    const endIndex = nextPage * QUICK_ANSWERS_PER_PAGE;
    setQuickAnswer(allQuickAnswers.slice(0, endIndex));
    setQuickAnswersPage(nextPage);
    setHasMoreQuickAnswers(endIndex < allQuickAnswers.length);
  };

  // Watch inputMessage for "/" to load quick answers and templates
  // Must be after handleLoadQuickAnswer definition
  useEffect(() => {
    if (inputMessage && inputMessage.startsWith("/")) {
      // Debounced — this used to fire the quick-messages fetch on EVERY
      // keystroke after the leading slash.
      const t = setTimeout(() => handleLoadQuickAnswer(inputMessage), 250);
      return () => clearTimeout(t);
    } else {
      setTypeBar(false);
    }
  }, [inputMessage]);

  const fetchFileLists = async () => {
    try {
        const { data } = await api.get("/files/");
        setFiles(data.files)
        setFileBar(true)
    } catch (err) {
        toastError(err);
    }
  };

  const handleOpenTemplatesList = async () => {
    if (!templatesBar) {
      try {
        const templates = await getQuickMenuTemplates();
        setTemplatesList(templates);
        setTemplatesBar(true);
        setFileBar(false); // Close files list if open
      } catch (err) {
        console.log("Could not load templates:", err);
        toastError(err);
      }
    } else {
      setTemplatesBar(false);
    }
  };

  const handleTemplateClick = (template) => {
    if (ticketLagsRoute()) return; // modal would send into the previous ticket
    setSelectedTemplate(template);
    setTemplateModalOpen(true);
    setTemplatesBar(false);
  };

  const handleStartRecording = async () => {
    if(disableOption)return;
    // 2026-06-12: voice notes record as mp3 (Mp3Recorder) and are now sendable
    // on FB/IG/Telegram via the media path, so recording is no longer gated off.
    setLoading(true);
    try {
      // navigator.mediaDevices is undefined on non-HTTPS origins (Android Chrome, etc.)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(i18n.t("messagesInput.micRequiresHttps") || "הקלטה קולית דורשת חיבור מאובטח (HTTPS)");
      }
      // Permission check only — RELEASE the mic immediately (Mp3Recorder opens
      // its own capture). Leaving this stream open held the microphone for the
      // whole tab and broke the browser softphone with NotReadableError.
      const permStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permStream.getTracks().forEach((t) => t.stop());
      await Mp3Recorder.start();
      setRecording(true);
      setLoading(false);
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleUploadAudio = async () => {
    if (ticketLagsRoute()) {
      // Mid-switch: stop and discard the take instead of posting it into the
      // previous ticket; leaving the recorder running would strand the mic.
      try {
        await Mp3Recorder.stop().getMp3();
      } catch (err) {
        /* recorder already stopped */
      }
      setRecording(false);
      return;
    }
    setLoading(true);
    try {
      const [, blob] = await Mp3Recorder.stop().getMp3();
      if (blob.size < 10000) {
        setLoading(false);
        setRecording(false);
        return;
      }

      const formData = new FormData();
      const filename = `audio-record-site-${new Date().getTime()}.mp3`;
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", true);

      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) {
      toastError(err);
    }

    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = async () => {
    try {
      await Mp3Recorder.stop().getMp3();
      setRecording(false);
    } catch (err) {
      toastError(err);
    }
  };

  // GPT AI Assistant handlers
  const handleOpenGPTMenu = (event) => {
    setGptMenuAnchor(event.currentTarget);
  };

  const handleCloseGPTMenu = () => {
    setGptMenuAnchor(null);
  };

  const handleOpenSuggestions = async () => {
    handleCloseGPTMenu();
    setGptModalOpen(true);
    setGptLoading(true);
    setGptSuggestions([]);
    
    try {
      const response = await getGPTSuggestions(ticketId, inputMessage || null);
      if (response && response.suggestions) {
        setGptSuggestions(response.suggestions);
        setGptConfidence(response.confidence || 0.8);
      }
    } catch (err) {
      console.error("GPT error:", err);
      // Don't show error toast - modal will show "no suggestions"
    } finally {
      setGptLoading(false);
    }
  };

  const handleGenerateGPT = async (customPrompt) => {
    setGptLoading(true);
    try {
      const response = await getGPTSuggestions(ticketId, inputMessage || null, customPrompt);
      if (response && response.suggestions) {
        setGptSuggestions(response.suggestions);
        setGptConfidence(response.confidence || 0.8);
      }
    } catch (err) {
      console.error("GPT error:", err);
      toastError(err);
    } finally {
      setGptLoading(false);
    }
  };

  const handleSelectGPTSuggestion = (suggestion) => {
    // Insert the suggestion into the input field
    const text = suggestion.text || suggestion;
    // Only arm the caret-restore when the text actually changes. If `text`
    // equals the current value, setInputMessage is a no-op (React bails the
    // re-render), the [inputMessage] layout effect never runs, and a non-null
    // pendingSelectionRef would otherwise leak into the user's NEXT keystroke
    // and yank the caret to the end. Guarding here keeps that from happening.
    if (text !== inputMessage) {
      pendingSelectionRef.current = (text || "").length;
    }
    setInputMessage(text);
    inputRef.current?.focus({ preventScroll: true });
  };

  const handleOpenSummary = () => {
    handleCloseGPTMenu();
    setSummaryModalOpen(true);
  };

  const handleOpenSales = () => {
    handleCloseGPTMenu();
    setSalesModalOpen(true);
  };

  const handleOpenSentiment = () => {
    handleCloseGPTMenu();
    setSentimentModalOpen(true);
  };

  const isGroup = showTabGroups && ticket.isGroup;
  // `recording` used to live in this formula, which was wrong: the
  // send-recording (CheckCircleOutline) and cancel-recording
  // (HighlightOff) buttons are ONLY rendered while recording is
  // true, and they read `disabled={disableOption}` — so as soon as
  // the user hit the mic they couldn't stop or send the take
  // (Raviv: "нажимаю на галочку нічого не проісходить"). The mic
  // button isn't rendered during recording anyway, so dropping
  // `recording` from this formula doesn't re-enable anything that
  // shouldn't be enabled.
  const disableOption = (!isGroup && loading) || ticketStatus === "closed";
  // 2026-06-11: WhatsApp-only affordances (catalog Files, WhatsApp Templates)
  // hard-fail on Facebook/Instagram/Telegram tickets — hide them there.
  const isMetaChannel = ["facebook", "instagram", "telegram"].includes(ticket?.channel);

  const renderReplyingMessage = (message) => {
    return (
      <div className={classes.replyginMsgWrapper}>
        <div className={classes.replyginMsgContainer}>
          <span
            className={clsx(classes.replyginContactMsgSideColor, {
              [classes.replyginSelfMsgSideColor]: !message.fromMe,
            })}
          ></span>
          {replyingMessage && (
            <div className={classes.replyginMsgBody}>
              <span className={classes.messageContactName}>
                {i18n.t("messagesInput.replying")}{" "}
                {/* Raviv 2026-05-30: for incoming messages show the contact name;
                    for outgoing messages (fromMe=true) the message has no contact
                    record (it was sent by an agent/user), so fall back to the
                    user's own name to avoid a blank "Replying to " label. */}
                {message.fromMe
                  ? (user?.name || i18n.t("messagesInput.replyingMe", { defaultValue: "Me" }))
                  : (message.contact?.name || "")}
              </span>
              <MarkdownWrapper>
                {message.body}
              </MarkdownWrapper>
            </div>
          )}
          {editingMessage && (
            <div className={classes.replyginMsgBody}>
              <span className={classes.messageContactName}>
                {i18n.t("messagesInput.editing")}
              </span>
              <MarkdownWrapper>
                {message.body}
              </MarkdownWrapper>
            </div>
          )}
        </div>
        <IconButton
          aria-label="showRecorder"
          component="span"
          disabled={disableOption}
          onClick={() => {
              setReplyingMessage(null);
              setEditingMessage(null);
              setInputMessage("");
          }}
        >
          <ClearIcon className={classes.sendMessageIcons} />
        </IconButton>
      </div>
    );
  };

  return (
      <>
      {isDragging && (
        <div className={classes.dropOverlay}>
          <div className={classes.dropOverlayInner}>
            <CloudUploadIcon className={classes.dropOverlayIcon} />
            <div className={classes.dropOverlayTitle}>
              {i18n.t("messagesInput.drop.title", "Drop files to send")}
            </div>
            <div className={classes.dropOverlaySub}>
              {i18n.t(
                "messagesInput.drop.sub",
                "Photos, videos, documents — preview before sending"
              )}
            </div>
          </div>
        </div>
      )}
      <MediaPreviewModal
        files={medias}
        loading={loading}
        progress={percentLoading}
        onCancel={() => {
          if (loading) return;
          setMedias([]);
          setPercentLoading(0);
        }}
        onSend={(caption) => handleUploadMedia(caption)}
        onAddMore={(extra) => setMedias((prev) => [...prev, ...extra])}
        onRemoveAt={(idx) =>
          setMedias((prev) => prev.filter((_, i) => i !== idx))
        }
      />
      <Paper square elevation={0} className={classes.mainWrapper}>
        {(replyingMessage && renderReplyingMessage(replyingMessage)) || (editingMessage && renderReplyingMessage(editingMessage))}
        <div className={classes.newMessageBox}>
          {/* Dropdowns - shown above the button row */}
          {fileBar && (
            <ul className={classes.messageQuickAnswersWrapper}>
              {files?.map((value, index) => (
                <ListItem button className={classes.listItem} key={index} onClick={() => handleFileClick(value)}>
                  <ListItemText primary={`${value.name} - ${value.message}`} />
                </ListItem>
              ))}
            </ul>
          )}
          
          {templatesBar && templatesList.length > 0 && (
            <ul className={classes.messageQuickAnswersWrapper}>
              {templatesList.map((template, index) => (
                <ListItem 
                  button 
                  className={classes.listItem} 
                  key={index} 
                  onClick={() => handleTemplateClick(template)}
                >
                  <ListItemText 
                    primary={`📋 ${template.name}`} 
                    secondary={template.bodyText?.substring(0, 50) + (template.bodyText?.length > 50 ? "..." : "")}
                  />
                </ListItem>
              ))}
            </ul>
          )}
          {templatesBar && templatesList.length === 0 && (
            <ul className={classes.messageQuickAnswersWrapper}>
              <ListItem className={classes.listItem}>
                <ListItemText 
                  primary={i18n.t("messageInput.noTemplates") || "No templates available"} 
                  secondary={i18n.t("messageInput.syncTemplates") || "Sync templates from Settings"} 
                />
              </ListItem>
            </ul>
          )}

          {/* Buttons row - ABOVE input */}
          <div className={classes.buttonsRow}>
            {!isMetaChannel && (
              <IconButton
                aria-label="files"
                component="span"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleOpenFileListModal()}
                disabled={disableOption}
                title="Files"
              >
                <CollectionsBookmarkIcon className={classes.sendMessageIcons} style={{ color: fileBar ? "#4caf50" : undefined }} />
              </IconButton>
            )}

            {!isMetaChannel && (
              <IconButton
                aria-label="templates"
                component="span"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleOpenTemplatesList}
                disabled={disableOption}
                title="WhatsApp Templates"
              >
                <DescriptionIcon className={classes.sendMessageIcons} style={{ color: templatesBar ? "#4caf50" : undefined }} />
              </IconButton>
            )}

            <IconButton
              aria-label="ai-assistant"
              component="span"
              onClick={handleOpenGPTMenu}
              disabled={disableOption}
              title="🤖 AI Assistant - GPT Features"
              style={{ 
                background: Boolean(gptMenuAnchor) ? "linear-gradient(135deg, #25d366 0%, #128c7e 100%)" : undefined,
                borderRadius: Boolean(gptMenuAnchor) ? "50%" : undefined,
              }}
            >
              <AutoAwesomeIcon 
                className={classes.sendMessageIcons} 
                style={{ 
                  color: Boolean(gptMenuAnchor) ? "#fff" : "#9c27b0",
                  fontSize: 22,
                }} 
              />
            </IconButton>
            
            {/* GPT Features Menu */}
            <Menu
              anchorEl={gptMenuAnchor}
              open={Boolean(gptMenuAnchor)}
              onClose={handleCloseGPTMenu}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
            >
              <MenuItem onClick={handleOpenSuggestions}>
                <ListItemIcon style={{ minWidth: 36 }}>
                  <AutoAwesomeIcon fontSize="small" style={{ color: "#9c27b0" }} />
                </ListItemIcon>
                <ListItemText primary={`💡 ${i18n.t("gptMenu.aiSuggestions")}`} />
              </MenuItem>
              <MenuItem onClick={handleOpenSummary}>
                <ListItemIcon style={{ minWidth: 36 }}>
                  <AutoAwesomeIcon fontSize="small" style={{ color: "#2196f3" }} />
                </ListItemIcon>
                <ListItemText primary={`📝 ${i18n.t("gptMenu.conversationSummary")}`} />
              </MenuItem>
              <MenuItem onClick={handleOpenSales}>
                <ListItemIcon style={{ minWidth: 36 }}>
                  <AutoAwesomeIcon fontSize="small" style={{ color: "#4caf50" }} />
                </ListItemIcon>
                <ListItemText primary={`💰 ${i18n.t("gptMenu.salesOpportunities")}`} />
              </MenuItem>
              {/* Sentiment Analysis - only for admins/managers */}
              {user?.profile === "admin" && (
                <MenuItem onClick={handleOpenSentiment}>
                  <ListItemIcon style={{ minWidth: 36 }}>
                    <AutoAwesomeIcon fontSize="small" style={{ color: "#ff9800" }} />
                  </ListItemIcon>
                  <ListItemText primary={`📊 ${i18n.t("gptMenu.sentimentAnalysis")}`} />
                </MenuItem>
              )}
            </Menu>

            <FileInput
              disableOption={disableOption}
              handleChangeMedias={handleChangeMedias}
            />

            {/* Phase 3.5 — Channel Switcher */}
            <ChannelSwitcherDropdown
              activeChannel={ticket?.channel || "whatsapp"}
              channelLinks={channelLinks}
              onSwitch={(ticketId) => {
                window.location.assign(`/tickets/${ticketId}`);
              }}
            />

            <EmojiOptions
              disabled={disableOption}
              handleAddEmoji={handleAddEmoji}
              showEmoji={showEmoji}
              setShowEmoji={setShowEmoji}
              onBeforeOpen={handleSaveCursorPosition}
            />

            <SignSwitch
              width={props.width}
              setSignMessage={setSignMessage}
              signMessage={signMessage}
            />

            {/* Voice: bridge-call button — placed after the signature toggle,
                styled to match the composer icon row. Same flow as the header
                call button (Whapiz→Telnyx→agent mobile→customer bridge; NO
                browser audio). Renders null unless Voice is on; isolated so it
                can never break the composer. */}
            <ErrorBoundary label="ComposerCallButton" silent>
              <VoiceCallButton ticket={ticket} iconClassName={classes.sendMessageIcons} />
            </ErrorBoundary>
            {/* Apple-style keypad dialer (call any number from the system) —
                lives in the composer bar next to the quick-call button. */}
            <ErrorBoundary label="ComposerDialer" silent>
              <SoftphoneDialer ticket={ticket} iconClassName={classes.sendMessageIcons} />
            </ErrorBoundary>
          </div>

          {/* Input row */}
          <div className={classes.inputMessageWrapper}>
            <CustomInput
            setFileBar={setFileBar}
            loading={loading}
            inputRef={inputRef}
            cursorPositionRef={cursorPositionRef}
            ticketStatus={(isGroup && "open" ) || ticketStatus}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            draftKey={draftKey}

            // handleChangeInput={handleChangeInput}
            handleSendMessage={handleSendMessage}
            handleInputPaste={handleInputPaste}
            disableOption={disableOption}
          />
            {typeBar ? (
              <ul className={classes.messageQuickAnswersWrapper}>
                {quickAnswers.map((value, index) => {
                  return (
                    <li
                      className={classes.listItem}
                      key={index}
                    >
                      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                      <a onClick={() => handleQuickAnswersClick(value)}>
                        {value.isTemplate ? (
                          <span style={{ color: "#4caf50" }}>{`${value.shortcut}`}</span>
                        ) : (
                          `${value.shortcut} - ${value.message}`
                        )}
                      </a>
                    </li>
                  );
                })}
                {hasMoreQuickAnswers && (
                  <li 
                    className={classes.listItem}
                    style={{ 
                      textAlign: "center", 
                      borderTop: "1px solid rgba(0,0,0,0.1)",
                      marginTop: 8,
                      paddingTop: 8
                    }}
                  >
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a 
                      onClick={handleLoadMoreQuickAnswers}
                      style={{ 
                        color: "#25d366", 
                        fontWeight: 500,
                        display: "block",
                        padding: "8px 12px"
                      }}
                    >
                      {`⬇️ הצג עוד (${allQuickAnswers.length - quickAnswers.length})`}
                    </a>
                  </li>
                )}
              </ul>
            ) : (
              <div></div>
            )}
          <ActionButtons
            inputMessage={inputMessage}
            loading={loading}
            recording={recording}
            ticketStatus={ticketStatus}
            disableOption={disableOption}
            handleSendMessage={handleSendMessage}
            handleCancelAudio={handleCancelAudio}
            handleUploadAudio={handleUploadAudio}
            handleStartRecording={handleStartRecording}
            handleScheduleClick={() => {
              // Same mid-switch guard as the send paths: the schedule modal
              // captures ticket={ticket}, which may still be the PREVIOUS one.
              if (ticketLagsRoute()) return;
              setScheduleModalOpen(true);
            }}
          />
        </div>
        </div>
      </Paper>
      
      <ScheduleMessageModal
        open={scheduleModalOpen}
        onClose={(scheduled) => {
          setScheduleModalOpen(false);
          if (scheduled) {
            setInputMessage(""); // Clear message after scheduling
          }
        }}
        ticket={ticket}
        message={inputMessage}
      />
      
      <SendTemplateModal
        open={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        ticketId={ticketId}
        onSuccess={() => {
          setTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
      />

      <GPTSuggestionsModal
        open={gptModalOpen}
        onClose={() => {
          setGptModalOpen(false);
          setGptSuggestions([]);
        }}
        suggestions={gptSuggestions}
        confidence={gptConfidence}
        onSelectSuggestion={handleSelectGPTSuggestion}
        loading={gptLoading}
        onGenerate={handleGenerateGPT}
        companyId={user?.companyId}
      />

      <ConversationSummaryModal
        open={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        ticketId={ticketId}
      />

      <SalesOpportunitiesModal
        open={salesModalOpen}
        onClose={() => setSalesModalOpen(false)}
        ticketId={ticketId}
      />

      <SentimentTrendsModal
        open={sentimentModalOpen}
        onClose={() => setSentimentModalOpen(false)}
        ticketId={ticketId}
      />
      </>
    );
};

export default withWidth()(MessageInputCustom);